import {
  AppShell,
  Avatar,
  Box,
  Burger,
  Button,
  Group,
  NavLink,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBrandAmazon,
  IconBuilding,
  IconFiles,
  IconListDetails,
  IconLogout,
  IconReceipt,
  IconScissors,
  IconBuildingStore,
  IconDownload,
  IconLayersIntersect,
  IconShieldLock,
  IconUser,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '../stores/auth-store';

export function AppLayout({ children }: { children?: ReactNode }) {
  const [opened, { toggle, close }] = useDisclosure();
  const user = useAuthStore((state) => state.user)!;
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (path: string) => {
    void navigate(path);
    close();
  };

  return (
    <AppShell
      header={{ height: 68 }}
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding='xl'
    >
      <AppShell.Header px='lg'>
        <Group h='100%' justify='space-between'>
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom='sm' size='sm' />
            <Box className='brand-mark'>D</Box>
            <div>
              <Title order={3}>Disit Internal</Title>
              <Text size='xs' c='dimmed'>
                Shared company workspace
              </Text>
            </div>
          </Group>
          <Group gap='sm'>
            <Avatar color='blue' radius='xl'>
              {user.email.slice(0, 1).toUpperCase()}
            </Avatar>
            <Box visibleFrom='xs'>
              <Text size='sm' fw={600}>
                {user.email}
              </Text>
              <Text size='xs' c='dimmed' tt='capitalize'>
                {user.role}
              </Text>
            </Box>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p='md'>
        <Stack justify='space-between' h='100%'>
          <Stack gap='xs'>
            <NavLink
              label='Files'
              leftSection={<IconFiles size={19} />}
              active={location.pathname === '/files'}
              onClick={() => go('/files')}
              variant='filled'
            />
            <NavLink
              label='App downloads'
              leftSection={<IconDownload size={19} />}
              active={location.pathname === '/downloads'}
              onClick={() => go('/downloads')}
              variant='filled'
            />
            <NavLink
              label='Amazon Selling'
              leftSection={<IconBrandAmazon size={19} />}
              active={location.pathname.startsWith('/amazon')}
              onClick={() => go('/amazon')}
              variant='filled'
            />
            <NavLink
              label='Meesho Cropper'
              leftSection={<IconScissors size={19} />}
              active={location.pathname === '/meesho-cropper'}
              onClick={() => go('/meesho-cropper')}
              variant='filled'
            />
            <NavLink
              label='Laser CAD Studio'
              leftSection={<IconLayersIntersect size={19} />}
              active={location.pathname === '/laser-studio'}
              onClick={() => go('/laser-studio')}
              variant='filled'
            />
            <NavLink
              label='Suppliers'
              leftSection={<IconBuildingStore size={19} />}
              active={location.pathname.startsWith('/suppliers')}
              onClick={() => go('/suppliers')}
              variant='filled'
            />
            <NavLink
              label='Business information'
              leftSection={<IconBuilding size={19} />}
              active={location.pathname === '/business-information'}
              onClick={() => go('/business-information')}
              variant='filled'
            />
            <NavLink
              label='Expenses'
              leftSection={<IconReceipt size={19} />}
              active={location.pathname === '/expenses'}
              onClick={() => go('/expenses')}
              variant='filled'
            />
            {user.role === 'admin' && (
              <>
                <NavLink
                  label='Users'
                  leftSection={<IconShieldLock size={19} />}
                  active={location.pathname === '/users'}
                  onClick={() => go('/users')}
                  variant='filled'
                />
                <NavLink
                  label='Server logs'
                  leftSection={<IconListDetails size={19} />}
                  active={location.pathname === '/logs'}
                  onClick={() => go('/logs')}
                  variant='filled'
                />
              </>
            )}
            <NavLink
              label='My account'
              leftSection={<IconUser size={19} />}
              active={location.pathname === '/profile'}
              onClick={() => go('/profile')}
              variant='filled'
            />
          </Stack>
          <Button
            variant='subtle'
            color='gray'
            leftSection={<IconLogout size={18} />}
            justify='flex-start'
            onClick={() => {
              logout();
              void navigate('/login');
            }}
          >
            Sign out
          </Button>
        </Stack>
      </AppShell.Navbar>
      <AppShell.Main>{children ?? <Outlet />}</AppShell.Main>
    </AppShell>
  );
}
